import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VirtualList from 'react-tiny-virtual-list';
import AutoSizer from 'react-virtualized-auto-sizer';

import { TemplatePlayer } from '../../types/team';
import { Paginator } from '../Paginator';

import { ItemAssistant, VariantsAssistance } from './ItemsAssistant';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { SelectValue } from '@/types';
import { Goleadores, JugNoJuegan, LeyDelEx } from '@/types/fieldAssistance';
import {
  EMPTY_TEMPLATE_PLAYER,
  FADE_IN_OUT,
  sortAlphabeticByLastName,
  sortByGolThenLastName,
} from '@/utils';

type DataPlayersProps = Goleadores[] | LeyDelEx[] | JugNoJuegan[];

interface Props extends VariantsAssistance {
  dataPlayersProps: DataPlayersProps;
  positionSelected: SelectValue;
  dontSort?: boolean;
}

export interface GroupByGols {
  [index: number]: TemplatePlayer[];
}

export const DefaultTable = ({
  dataPlayersProps,
  positionSelected,
  variant = 'DEFAULT',
  dontSort = false,
}: Props) => {
  const vlistRef = useRef<VirtualList>(null); // RefObject<VirtualList>
  const playerTemplateMap = usePlayerTemplateMap();
  const { isMobile } = useIsDesktop();
  const [contentCurrentPage, setContentCurrentPage] = useState<
    TemplatePlayer[]
  >([]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [positionSelected.value]);

  const playersPositionSelected = useMemo(() => {
    const players: TemplatePlayer[] = dataPlayersProps.map((player) => {
      return playerTemplateMap.get(player.idJT) || EMPTY_TEMPLATE_PLAYER;
    });

    const filteredPlayers: TemplatePlayer[] = players.flatMap(
      (player: TemplatePlayer) => {
        if (
          positionSelected.value === 0 ||
          player?.p === positionSelected.value
        ) {
          return [player];
        }
        return [];
      }
    );

    if (dontSort) {
      return filteredPlayers;
    }

    if (variant === 'GOL') {
      return sortByGolThenLastName(filteredPlayers);
    }
    return sortAlphabeticByLastName(filteredPlayers);
  }, [
    variant,
    playerTemplateMap,
    dataPlayersProps,
    positionSelected.value,
    dontSort,
  ]);

  const fieldPlayersMap = useMemo(() => {
    const map = new Map<number, Goleadores | LeyDelEx | JugNoJuegan>();
    dataPlayersProps.forEach((player) => {
      map.set(player.idJT, player);
    });
    return map;
  }, [dataPlayersProps]);

  const getDataFieldPlayer = useCallback(
    (id: number): Goleadores | LeyDelEx | JugNoJuegan | undefined => {
      return fieldPlayersMap.get(id);
    },
    [fieldPlayersMap]
  );

  if (isMobile) {
    return (
      playersPositionSelected &&
      playersPositionSelected.length > 0 && (
        <div className="h-screen mt-5 rounded-lg bg-white">
          <AutoSizer className="h-full lg:h-[1002px]">
            {({ height, width }) => {
              return (
                <VirtualList
                  ref={vlistRef}
                  height={height}
                  itemCount={playersPositionSelected.length}
                  itemSize={80}
                  renderItem={({ index, style }) => {
                    const player = playersPositionSelected[index];
                    if (player) {
                      return (
                        <div key={index} style={style}>
                          <ItemAssistant.Default
                            dataPlayerFieldAssistance={getDataFieldPlayer(
                              player.id
                            )}
                            templatePlayer={player}
                            variant={variant}
                          />
                        </div>
                      );
                    }
                  }}
                  width={width}
                />
              );
            }}
          </AutoSizer>
        </div>
      )
    );
  }

  const handleContentCurrentPage = (players: TemplatePlayer[]) => {
    players && setContentCurrentPage(players);
  };

  if (
    !Array.isArray(playersPositionSelected) ||
    playersPositionSelected.length === 0
  ) {
    return null;
  }

  const itemsToRender =
    Array.isArray(contentCurrentPage) && contentCurrentPage.length > 0
      ? contentCurrentPage
      : playersPositionSelected;

  return (
    <motion.div
      key={variant}
      animate="show"
      initial="hide"
      variants={FADE_IN_OUT}
    >
      <div className="lg:h-auto mt-6 rounded-lg bg-white">
        {itemsToRender.map((player: TemplatePlayer, index: number) =>
          player ? (
            <ItemAssistant.Default
              key={`${player.id ?? 'p'}-${index}`}
              dataPlayerFieldAssistance={getDataFieldPlayer(player.id)}
              templatePlayer={player}
              variant={variant}
            />
          ) : (
            ''
          )
        )}

        {playersPositionSelected.length > 10 && (
          <Paginator
            key={`${variant}-${positionSelected?.label ?? 'none'}`}
            currentContentPage={handleContentCurrentPage}
            items={playersPositionSelected}
          />
        )}
      </div>
    </motion.div>
  );
};
